import React, { useMemo, useState } from 'react';
import { connect } from 'dva';

import { Button, Spin, Tooltip } from 'antd';
import { EditOutlined, MinusCircleOutlined, SearchOutlined } from '@ant-design/icons';

import { overridesColumns } from '../../Templates/columns';
import RuleSearch from './RuleSearch';
import { TabsWrapper } from '../styles';
import EditRulesModal from './EditRulesModal';
import {
  ADINVESTIGATOR_VIEW_RULES_TABLE,
  ADINVESTIGATOR_VIEW_RULES_TABLE_EDIT_TOOLTIP,
  ADINVESTIGATOR_VIEW_RULES_TABLE_REMOVE_TOOLTIP,
} from '../../../testUtils';
import { ResizableTable } from '../../Templates/ResizableTable';
import { AD_INVESTIGATOR, EDIT, EDIT_ALL, USER_PROFILE } from '../../../constants';

const Y_SCROLL = { y: '50vh' };

const ViewExistingOverrideRules = ({
  dispatch,
  rules = [],
  loading,
  overrideBy,
  setOverrideBy,
  userWidths = [],
  deviceWidths = [],
  permissions,
  currentUserEmail,
}) => {
  const [selectedOverride, setSelectedOverride] = useState(null);
  const isViewOnly = permissions.cannot(EDIT, AD_INVESTIGATOR);
  const canEditAllOverrides = permissions.can(EDIT_ALL, AD_INVESTIGATOR);

  const editRuleRender = ({ editorEmail, key: overrideKey }) => {
    const isEditor = editorEmail?.toLowerCase() === currentUserEmail.toLowerCase();
    const canEdit = !isViewOnly && (isEditor || canEditAllOverrides || editorEmail == null);
    return canEdit ? (
      <Tooltip title="Edit this rule" data-testid={ADINVESTIGATOR_VIEW_RULES_TABLE_EDIT_TOOLTIP}>
        <Button
          shape="circle"
          icon={
            <EditOutlined
              onClick={() => {
                setSelectedOverride(overrideKey);
              }}
            />
          }
        />
      </Tooltip>
    ) : null;
  };

  const removeRuleRender = (values) => {
    const isEditor = values?.editorEmail?.toLowerCase() === currentUserEmail.toLowerCase();
    const canRemove = !isViewOnly && (isEditor || canEditAllOverrides || values?.editorEmail == null);
    return canRemove ? (
      <Tooltip title="Remove this rule" data-testid={ADINVESTIGATOR_VIEW_RULES_TABLE_REMOVE_TOOLTIP}>
        <Button
          shape="circle"
          icon={
            <MinusCircleOutlined
              onClick={() => {
                return dispatch({ type: 'adInvestigator/removeRule', payload: values });
              }}
            />
          }
        />
      </Tooltip>
    ) : null;
  };
  const handleUserWidthChange = (widths) =>
    dispatch({
      type: 'adInvestigator/overrideUserWidths',
      payload: widths,
    });
  const handleDeviceWidthChange = (widths) =>
    dispatch({
      type: 'adInvestigator/overrideDeviceWidths',
      payload: widths,
    });

  const performSearch = (searchVal, searchTerm) => {
    return searchVal != null && searchVal.toString().toLowerCase().includes(searchTerm.toLowerCase());
  };

  const searchableProps = useMemo(
    () => ({
      inputProps: {
        placeholder: 'Search in table...',
        prefix: <SearchOutlined />,
      },
      searchFunction(dataSource, searchTerm) {
        return (dataSource ?? []).filter((row) => {
          return Object.values(row).some((val) => {
            if (Array.isArray(val)) {
              return val.some((item) => performSearch(item, searchTerm));
            }
            return performSearch(val, searchTerm);
          });
        });
      },
    }),
    []
  );

  const renderResizableTable = (overrideBy, widths, widthHandler) => (
    <ResizableTable
      refresh
      inputColumns={overridesColumns(overrideBy, editRuleRender, removeRuleRender, !isViewOnly, widths)}
      dataSource={rules}
      scroll={Y_SCROLL}
      pagination={false}
      widthHandler={widthHandler}
      searchableProps={searchableProps}
    />
  );

  return (
    <Spin spinning={loading}>
      <TabsWrapper data-testid={ADINVESTIGATOR_VIEW_RULES_TABLE}>
        <RuleSearch overrideBy={overrideBy} setOverrideBy={setOverrideBy} />
        {overrideBy === USER_PROFILE
          ? renderResizableTable(overrideBy, userWidths, handleUserWidthChange)
          : renderResizableTable(overrideBy, deviceWidths, handleDeviceWidthChange)}
      </TabsWrapper>
      {/* Modals */}
      <EditRulesModal
        overrideBy={overrideBy}
        selectedOverride={selectedOverride}
        rules={rules}
        setSelectedOverride={setSelectedOverride}
      />
    </Spin>
  );
};

function mapStateToProps({ app, adInvestigator, loading }) {
  return {
    rules: adInvestigator.rules,
    userWidths: adInvestigator.userWidths,
    deviceWidths: adInvestigator.deviceWidths,
    loading: loading.effects['adInvestigator/fetchExistingRules'],
    permissions: app.currentUser.permissions,
    currentUserEmail: app.currentUser.email,
  };
}
export default connect(mapStateToProps)(ViewExistingOverrideRules);
